/**
 * Client Bridge Hook
 * Fetches the bridge name for the current client on mount.
 * Bridge configuration is stable so no polling is needed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useClientsApi } from "@/services/api/clients-api";

interface ClientBridgeState {
    bridgeName: string | null;
    hasBridge: boolean;
    /**
     * Whether Direct Sync (bridge export) is offered. False when there is no bridge OR the
     * client's export mode is Csv — CSV-mode clients keep their bridge for vendor lookups
     * only, so bridge health stays visible while Direct Sync export is hidden.
     */
    canDirectSync: boolean;
    isLoading: boolean;
}

export function useClientBridge() {
    const [state, setState] = useState<ClientBridgeState>({
        bridgeName: null,
        hasBridge: false,
        canDirectSync: false,
        isLoading: true,
    });

    const clientsApi = useClientsApi();

    const fetch = useCallback(async () => {
        try {
            const response = await clientsApi.getClientBridgeName();
            const hasBridge = !!response.bridgeName;
            setState({
                bridgeName: response.bridgeName,
                hasBridge,
                canDirectSync: hasBridge && response.exportMode !== "Csv",
                isLoading: false,
            });
        } catch {
            setState({
                bridgeName: null,
                hasBridge: false,
                canDirectSync: false,
                isLoading: false,
            });
        }
    }, [clientsApi]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return useMemo(() => state, [state]);
}
